import { makeAutoObservable, runInAction } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAuth, type AuthUser } from '@/services/AuthService';
import { type ILogger } from '@/services/Logger';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated' | 'error';

/**
 * The signed-in session ViewModel: owns the OIDC login/logout lifecycle and exposes the user's identity +
 * roles. `isAdmin` (the `admin` role in the access token) gates the admin console. Verified against `FakeAuth`.
 */
export class AuthStore {
  private _status: AuthStatus = 'loading';
  private _user: AuthUser | null = null;
  private _error: string | null = null;

  constructor(
    private auth: IAuth,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  get status(): AuthStatus {
    return this._status;
  }
  get isAuthenticated(): boolean {
    return this._status === 'authenticated';
  }
  get error(): string | null {
    return this._error;
  }
  get sub(): string | null {
    return this._user?.sub ?? null;
  }
  get accessToken(): string | null {
    return this._user?.accessToken ?? null;
  }
  /** Roles from the access token (the backend resolves them at mint from the User aggregate). */
  get roles(): string[] {
    const claims = this._user?.accessTokenClaims ?? {};
    const r = claims['roles'];
    return Array.isArray(r) ? (r as unknown[]).map(String) : [];
  }
  get isAdmin(): boolean {
    return this.roles.includes('admin');
  }
  /** The signed-in user's display identity (from the id token claims). */
  get username(): string | null {
    const claims = this._user?.idTokenClaims ?? {};
    return (claims['preferred_username'] as string | undefined) ?? null;
  }
  get email(): string | null {
    const claims = this._user?.idTokenClaims ?? {};
    return (claims['email'] as string | undefined) ?? null;
  }
  get name(): string | null {
    const claims = this._user?.idTokenClaims ?? {};
    return (claims['name'] as string | undefined) ?? null;
  }
  get emailVerified(): boolean {
    const claims = this._user?.idTokenClaims ?? {};
    return claims['email_verified'] === true;
  }
  get idTokenClaims(): Record<string, unknown> {
    return this._user?.idTokenClaims ?? {};
  }

  /** Process an OIDC redirect callback if present, else load the stored session. */
  async init(): Promise<void> {
    try {
      const user = await this.auth.init();
      runInAction(() => {
        this._user = user;
        this._status = user ? 'authenticated' : 'anonymous';
      });
    } catch (e) {
      this.logger.error('auth init failed', e);
      runInAction(() => {
        this._status = 'error';
        this._error = e instanceof Error ? e.message : String(e);
      });
    }
  }

  async login(): Promise<void> {
    await this.auth.login(); // navigates away (redirect)
  }

  async logout(): Promise<void> {
    await this.auth.logout(); // navigates away (RP-initiated end_session)
  }
}

export const TOKEN_AuthStore = token<AuthStore>('AuthStore');
